import api, { handleResponse, handleError } from './api';

const mimeService = {
  // === Guest Operations ===

  /**
   * Get all pending MIME requests for the current user
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getPendingRequests() {
    try {
      const response = await api.get('/mime/requests/pending');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a specific MIME request
   * @param {string} requestId - The MIME request ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getRequest(requestId) {
    try {
      const response = await api.get(`/mime/requests/${requestId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Respond to a MIME request (accept or decline)
   * @param {string} requestId - The MIME request ID
   * @param {boolean} accept - Whether to accept the request
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async respondToRequest(requestId, accept) {
    try {
      const response = await api.post(`/mime/requests/${requestId}/respond`, { accept });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get all MIME requests for the current user on a specific event
   * @param {string} eventId - The event ID
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getMyRequestsForEvent(eventId) {
    try {
      const response = await api.get(`/mime/events/${eventId}/my-requests`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // === Host Operations ===

  /**
   * Create a new MIME round for an event
   * @param {string} eventId - The event ID
   * @param {object} data - The MIME round data
   * @param {number} data.additionalCommitmentCents - Additional P$ commitment required
   * @param {string} [data.message] - Optional message to guests
   * @param {number} [data.shotClockMinutes=60] - Shot clock duration in minutes
   * @param {boolean} [data.removeNonResponders=false] - Whether to remove non-responders
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async createRound(eventId, data) {
    try {
      const response = await api.post(`/mime/events/${eventId}/rounds`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a specific MIME round
   * @param {string} roundId - The MIME round ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getRound(roundId) {
    try {
      const response = await api.get(`/mime/rounds/${roundId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get MIME summary for an event
   * @param {string} eventId - The event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEventSummary(eventId) {
    try {
      const response = await api.get(`/mime/events/${eventId}/summary`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get all requests for a specific MIME round
   * @param {string} roundId - The MIME round ID
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getRoundRequests(roundId) {
    try {
      const response = await api.get(`/mime/rounds/${roundId}/requests`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Cancel an active MIME round
   * @param {string} roundId - The MIME round ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async cancelRound(roundId) {
    try {
      const response = await api.post(`/mime/rounds/${roundId}/cancel`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // === Helpers ===

  /**
   * Format seconds remaining as MM:SS
   * @param {number} seconds - Seconds remaining
   * @returns {string} - Formatted time like "05:30"
   */
  formatTimeRemaining(seconds) {
    if (seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  },

  /**
   * Format P$ amount for display
   * @param {number} cents - Amount in cents
   * @returns {string} - Formatted string like "P$10.00"
   */
  formatAmount(cents) {
    const dollars = cents / 100;
    return `P$${dollars.toFixed(2)}`;
  },
};

export default mimeService;
